import React, { useState, useEffect } from "react";
import {
  LayoutDashboard,
  ClipboardList,
  Building2,
  MapPin,
  LogOut,
  Plus,
  X,
  Search,
  Trash2,
  Check,
  Edit2,
  Calendar,
  User,
  Car,
  Sparkles,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Eye,
  KeyRound,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  ref,
  push,
  set,
  onValue,
  remove,
  update,
  get,
} from "firebase/database";
import {
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, database, db, getSecondaryAuth } from "./firebase";

const PRESTATIONS = [
  "Nettoyage intérieur",
  "Nettoyage extérieur",
  "Nettoyage complet (intérieur + extérieur)",
  "Lustrage / Polish",
  "Pressing des sièges",
  "Désinfection véhicule",
  "Nettoyage moteur",
  "Rénovation phares",
];

const STATES = ["En attente", "En cours", "Terminé", "Annulé"];

const DEFAULT_SITES = [
  "WASHINGTON PLAZA",
  "L'ORÉAL AULNAY",
  "SAMSUNG",
  "THALES CRISTAL",
  "CARREFOUR MASSY",
];

const STATUS_STYLES = {
  "En attente": { badge: "bg-yellow-100 text-yellow-700", border: "border-yellow-400", Icon: Clock },
  "En cours": { badge: "bg-blue-100 text-blue-700", border: "border-blue-400", Icon: AlertCircle },
  Terminé: { badge: "bg-green-100 text-green-700", border: "border-green-400", Icon: CheckCircle },
  Annulé: { badge: "bg-red-100 text-red-700", border: "border-red-400", Icon: X },
};

const GRADIENT = "linear-gradient(135deg, #667eea 0%, #142C93 100%)";

const NAV_ITEMS = [
  { id: "dashboard", label: "Tableau de bord", Icon: LayoutDashboard },
  { id: "prestations", label: "Prestations", Icon: ClipboardList },
  { id: "concierges", label: "Conciergeries", Icon: Building2 },
  { id: "sites", label: "Sites", Icon: MapPin },
];

export default function AdminPanel({ user }) {
  const [activeTab, setActiveTab] = useState("dashboard");

  const [tasks, setTasks] = useState([]);
  const [tasksPending, setTasksPending] = useState([]);
  const [concierges, setConcierges] = useState([]);
  const [sites, setSites] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterSite, setFilterSite] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expandedSites, setExpandedSites] = useState({});

  const [modal, setModal] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // ── Firebase listeners ──────────────────────────────────────────────

  useEffect(() => {
    const unsubs = [];

    unsubs.push(
      onValue(ref(database, "tasks"), (snap) => {
        const d = snap.val();
        setTasks(d ? Object.keys(d).map((id) => ({ id, ...d[id] })) : []);
      })
    );

    unsubs.push(
      onValue(ref(database, "tasksPending"), (snap) => {
        const d = snap.val();
        setTasksPending(d ? Object.keys(d).map((id) => ({ id, ...d[id] })) : []);
      })
    );

    unsubs.push(
      onValue(ref(database, "conciergeries"), (snap) => {
        const d = snap.val();
        if (d) {
          setConcierges(
            Object.keys(d).map((id) => {
              const val = d[id];
              if (typeof val === "string") return { id, name: val };
              return { id, ...val };
            })
          );
        } else {
          setConcierges([]);
        }
      })
    );

    unsubs.push(
      onValue(ref(database, "sites"), (snap) => {
        const d = snap.val();
        if (d) {
          setSites(Object.keys(d).map((id) => ({ id, ...d[id] })));
        } else {
          seedDefaultSites();
        }
      })
    );

    return () => unsubs.forEach((u) => u && u());
  }, []);

  const seedDefaultSites = async () => {
    for (const name of DEFAULT_SITES) {
      const newRef = push(ref(database, "sites"));
      await set(newRef, { name });
    }
  };

  const siteNames = sites.map((s) => s.name);

  // ── Prestation actions ──────────────────────────────────────────────

  const handleValidateTask = async (task) => {
    try {
      const { id, ...data } = task;
      const newRef = push(ref(database, "tasks"));
      await set(newRef, data);
      await remove(ref(database, `tasksPending/${id}`));
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  const handleDeleteTask = async (path, id) => {
    if (!window.confirm("Supprimer cette prestation ?")) return;
    try {
      await remove(ref(database, `${path}/${id}`));
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  const handleSavePrestation = async () => {
    if (!formData.carName || !formData.carImmatriculation) {
      alert("Véhicule et immatriculation sont obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      if (formData._editId) {
        const path = tasks.find((t) => t.id === formData._editId) ? "tasks" : "tasksPending";
        const { _editId, _editPath, ...clean } = formData;
        await update(ref(database, `${path}/${_editId}`), clean);
      } else {
        const newRef = push(ref(database, "tasksPending"));
        await set(newRef, {
          ...formData,
          state: formData.state || "En attente",
          createdAt: new Date().toISOString(),
        });
      }
      setModal(null);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Concierge actions ───────────────────────────────────────────────

  const handleSaveConcierge = async () => {
    if (formData._editId) {
      setSubmitting(true);
      try {
        const { _editId, ...data } = formData;
        await update(ref(database, `conciergeries/${_editId}`), data);
        setModal(null);
      } catch (err) {
        alert("Erreur : " + err.message);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!formData.name) {
      alert("Le nom est obligatoire.");
      return;
    }
    setSubmitting(true);
    try {
      const newRef = push(ref(database, "conciergeries"));
      await set(newRef, { ...formData });
      setModal(null);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConcierge = async (id) => {
    if (!window.confirm("Supprimer cette conciergerie ?")) return;
    try {
      await remove(ref(database, `conciergeries/${id}`));
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  const handleCreateLogin = async () => {
    if (!formData.email || !formData.password) {
      alert("Email et mot de passe sont obligatoires.");
      return;
    }
    if (formData.password.length < 6) {
      alert("Le mot de passe doit faire au moins 6 caractères.");
      return;
    }
    setSubmitting(true);
    try {
      const conciergeId = formData._conciergeId;
      const conciergerie = concierges.find((c) => c.id === conciergeId);
      const displayName = conciergerie?.name || conciergerie?.username || "";

      const sAuth = getSecondaryAuth();
      const cred = await createUserWithEmailAndPassword(sAuth, formData.email, formData.password);
      const uid = cred.user.uid;

      await set(ref(database, `users/${uid}`), {
        username: displayName,
        email: formData.email,
        role: "concierge",
        conciergerieId: conciergeId,
      });

      await setDoc(doc(db, "users", uid), {
        username: displayName,
        email: formData.email,
        role: "concierge",
        conciergerieId: conciergeId,
        createdAt: new Date(),
      });

      await update(ref(database, `conciergeries/${conciergeId}`), {
        email: formData.email,
        authUid: uid,
      });

      await firebaseSignOut(sAuth);
      setModal(null);
      alert(`Accès créé pour ${displayName} !\nEmail : ${formData.email}`);
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        alert("Cet email est déjà utilisé par un autre compte.");
      } else {
        alert("Erreur : " + err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Site actions ────────────────────────────────────────────────────

  const handleSaveSite = async () => {
    if (!formData.name?.trim()) {
      alert("Le nom du site est obligatoire.");
      return;
    }
    setSubmitting(true);
    try {
      const data = { name: formData.name.trim(), conciergerie: formData.conciergerie || "" };
      if (formData._editId) {
        await update(ref(database, `sites/${formData._editId}`), data);
      } else {
        const newRef = push(ref(database, "sites"));
        await set(newRef, data);
      }
      setModal(null);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSite = async (id) => {
    if (!window.confirm("Supprimer ce site ?")) return;
    try {
      await remove(ref(database, `sites/${id}`));
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────

  const getStatusStyle = (state) => STATUS_STYLES[state] || STATUS_STYLES["En attente"];

  const siteToConciergerie = {};
  sites.forEach((s) => {
    if (s.conciergerie) siteToConciergerie[s.name] = s.conciergerie;
  });

  const getConciergieName = (task) => {
    if (task.conciergerie) return task.conciergerie;
    if (task.site && siteToConciergerie[task.site]) return siteToConciergerie[task.site];
    if (task.conciergeName) return task.conciergeName;
    return null;
  };

  // ── Filtering ───────────────────────────────────────────────────────

  const allPrestations = [
    ...tasksPending.map((t) => ({ ...t, _source: "tasksPending" })),
    ...tasks.map((t) => ({ ...t, _source: "tasks" })),
  ];

  const filterByDate = (list) =>
    list.filter((t) => {
      const d = t.date || "";
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      return true;
    });

  const filteredPrestations = allPrestations.filter((t) => {
    const s = searchTerm.toLowerCase();
    const cName = getConciergieName(t) || "";
    const matchSearch =
      !s ||
      (t.carName || "").toLowerCase().includes(s) ||
      (t.carImmatriculation || "").toLowerCase().includes(s) ||
      cName.toLowerCase().includes(s) ||
      (t.nameClient || "").toLowerCase().includes(s);
    const matchState = !filterState || t.state === filterState || (filterState === "En attente" && t._source === "tasksPending");
    const matchSite = !filterSite || t.site === filterSite;
    const d = t.date || "";
    const matchDate = (!dateFrom || d >= dateFrom) && (!dateTo || d <= dateTo);
    return matchSearch && matchState && matchSite && matchDate;
  }).sort((a, b) => {
    const parseDate = (raw) => {
      if (!raw) return 0;
      if (typeof raw !== "string") {
        if (raw.toDate) return raw.toDate().getTime();
        if (raw.seconds) return raw.seconds * 1000;
        return 0;
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return new Date(raw).getTime() || 0;
      const parts = raw.split(/[-/.]/);
      if (parts.length === 3) {
        const [d, m, y] = parts;
        return new Date(`${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`).getTime() || 0;
      }
      return new Date(raw).getTime() || 0;
    };
    const da = parseDate(a.date) || parseDate(a.createdAt);
    const db = parseDate(b.date) || parseDate(b.createdAt);
    return db - da;
  });

  const hasDateFilter = dateFrom || dateTo;
  const clearDateFilters = () => { setDateFrom(""); setDateTo(""); };

  // ── Helpers ─────────────────────────────────────────────────────────

  const openCreatePrestation = () => {
    setFormData({
      prestationType: "",
      carName: "",
      carImmatriculation: "",
      nameClient: "",
      conciergerie: "",
      site: "",
      state: "En attente",
      date: new Date().toISOString().split("T")[0],
      heure: "",
      note: "",
    });
    setModal("prestation");
  };

  const openEditPrestation = (task) => {
    setFormData({ ...task, _editId: task.id, _editPath: task._source });
    setModal("prestation");
  };

  const openCreateConcierge = () => {
    setFormData({ name: "", email: "", phone: "" });
    setModal("concierge");
  };

  const openEditConcierge = (c) => {
    setFormData({ ...c, _editId: c.id });
    setModal("concierge");
  };

  const openCreateLogin = (c) => {
    setFormData({ email: c.email || "", password: "", _conciergeId: c.id });
    setModal("login");
  };

  const openCreateSite = () => {
    setFormData({ name: "", conciergerie: "" });
    setModal("site");
  };

  const openEditSite = (s) => {
    setFormData({ name: s.name, conciergerie: s.conciergerie || "", _editId: s.id });
    setModal("site");
  };

  // ── RENDER ──────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="text-white shadow-lg" style={{ background: GRADIENT }}>
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">MIVIA Services</h1>
              <p className="text-blue-200 text-sm">Administration</p>
            </div>
            <button
              onClick={() => auth.signOut()}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition flex items-center gap-2 text-sm"
            >
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </div>

      {/* Navigation tabs */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {NAV_ITEMS.map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                  activeTab === id
                    ? "border-indigo-600 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon size={18} />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === "dashboard" && renderDashboard()}
        {activeTab === "prestations" && renderPrestations()}
        {activeTab === "concierges" && renderConcierges()}
        {activeTab === "sites" && renderSites()}
      </div>

      {/* Modals */}
      {modal === "prestation" && renderPrestationModal()}
      {modal === "concierge" && renderConciergeModal()}
      {modal === "site" && renderSiteModal()}
      {modal === "viewPrestation" && renderViewPrestationModal()}
      {modal === "login" && renderLoginModal()}
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════
  // DASHBOARD
  // ══════════════════════════════════════════════════════════════════════

  function renderDashboard() {
    const pending = tasksPending.length;
    const validated = tasks.length;

    return (
      <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="En attente" value={pending} from="from-yellow-400" to="to-orange-500" sub="text-yellow-100" />
          <StatCard label="Validées" value={validated} from="from-blue-400" to="to-blue-600" sub="text-blue-100" />
          <StatCard label="Conciergeries" value={concierges.length} from="from-indigo-400" to="to-indigo-600" sub="text-indigo-100" />
          <StatCard label="Sites" value={sites.length} from="from-emerald-400" to="to-emerald-600" sub="text-emerald-100" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Clock size={18} className="text-yellow-500" />
              Dernières demandes en attente
            </h3>
            {tasksPending.length > 0 ? (
              <div className="space-y-3">
                {tasksPending.slice(0, 5).map((t) => (
                  <div key={t.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{t.carName || "—"}</p>
                      <p className="text-xs text-gray-500">
                        {getConciergieName(t) ? `${getConciergieName(t)} · ` : ""}{t.site || "—"} · {t.date || "—"}
                      </p>
                    </div>
                    <button
                      onClick={() => handleValidateTask(t)}
                      className="bg-green-100 text-green-700 px-3 py-1 rounded-lg text-xs font-semibold hover:bg-green-200 transition flex items-center gap-1"
                    >
                      <Check size={14} /> Valider
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm text-center py-4">Aucune demande en attente</p>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <CheckCircle size={18} className="text-blue-500" />
              Dernières prestations validées
            </h3>
            {tasks.length > 0 ? (
              <div className="space-y-3">
                {tasks.slice(0, 5).map((t) => (
                  <div key={t.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{t.carName || "—"}</p>
                      <p className="text-xs text-gray-500">
                        {t.prestationType && `${t.prestationType} · `}{t.site || "—"} · {t.date || "—"}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusStyle(t.state).badge}`}>
                      {t.state || "Validée"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm text-center py-4">Aucune prestation validée</p>
            )}
          </div>
        </div>
      </>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // PRESTATIONS
  // ══════════════════════════════════════════════════════════════════════

  function renderPrestations() {
    return (
      <>
        {/* Toolbar */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-3 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Rechercher par véhicule, immatriculation, client, conciergerie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:outline-none"
              />
            </div>
            <select
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
              className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:outline-none"
            >
              <option value="">Tous les statuts</option>
              {STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              value={filterSite}
              onChange={(e) => setFilterSite(e.target.value)}
              className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:outline-none"
            >
              <option value="">Tous les sites</option>
              {siteNames.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <button
              onClick={openCreatePrestation}
              className="text-white px-5 py-2.5 rounded-lg font-semibold text-sm flex items-center gap-2 shrink-0"
              style={{ background: GRADIENT }}
            >
              <Plus size={18} /> Nouvelle prestation
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-100">
            <span className="text-xs font-semibold text-gray-500 uppercase">Période :</span>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <Calendar size={14} className="text-gray-400" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="text-sm border-none outline-none bg-transparent text-gray-700 w-[130px]"
              />
            </div>
            <span className="text-gray-400 text-sm">→</span>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <Calendar size={14} className="text-gray-400" />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="text-sm border-none outline-none bg-transparent text-gray-700 w-[130px]"
              />
            </div>
            {hasDateFilter && (
              <button
                onClick={clearDateFilters}
                className="text-xs text-gray-500 hover:text-red-500 flex items-center gap-1 transition"
              >
                <X size={14} /> Effacer
              </button>
            )}
            <span className="text-xs text-gray-400 ml-auto">{filteredPrestations.length} résultat(s)</span>
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Table header */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <div className="col-span-2">Véhicule</div>
            <div className="col-span-2">Prestation</div>
            <div className="col-span-2">Conciergerie</div>
            <div className="col-span-1">Site</div>
            <div className="col-span-1">Date</div>
            <div className="col-span-1">Statut</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>

          {filteredPrestations.length > 0 ? (
            filteredPrestations.map((t) => {
              const style = getStatusStyle(t.state || (t._source === "tasksPending" ? "En attente" : "Validée"));
              const isPending = t._source === "tasksPending";
              return (
                <div
                  key={`${t._source}-${t.id}`}
                  className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 border-b border-gray-100 hover:bg-gray-50 transition items-center`}
                >
                  <div className="col-span-2">
                    <p className="font-semibold text-gray-800 text-sm">{t.carName || "—"}</p>
                    <p className="text-xs text-gray-400">{t.carImmatriculation || "—"}</p>
                  </div>
                  <div className="col-span-2 text-sm text-gray-600">{t.prestationType || "—"}</div>
                  <div className="col-span-2 text-sm text-gray-600">
                    {getConciergieName(t) ? (
                      <span className="flex items-center gap-1">
                        <Building2 size={14} className="text-indigo-400" />
                        {getConciergieName(t)}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </div>
                  <div className="col-span-1 text-sm text-gray-600 truncate">{t.site || "—"}</div>
                  <div className="col-span-1 text-sm text-gray-600">{t.date || (typeof t.createdAt === "string" ? t.createdAt.slice(0, 10) : "—")}</div>
                  <div className="col-span-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${style.badge}`}>
                      {t.state || (isPending ? "En attente" : "—")}
                    </span>
                  </div>
                  <div className="col-span-3 flex items-center justify-end gap-2">
                    <button
                      onClick={() => { setFormData(t); setModal("viewPrestation"); }}
                      className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                      title="Voir"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => openEditPrestation(t)}
                      className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                      title="Modifier"
                    >
                      <Edit2 size={16} />
                    </button>
                    {isPending && (
                      <button
                        onClick={() => handleValidateTask(t)}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                        title="Valider"
                      >
                        <Check size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteTask(t._source, t.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Supprimer"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-12">
              <ClipboardList size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-400">Aucune prestation trouvée</p>
            </div>
          )}
        </div>
      </>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // CONCIERGERIES
  // ══════════════════════════════════════════════════════════════════════

  function renderConcierges() {
    return (
      <>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Conciergeries</h2>
            <p className="text-sm text-gray-500">{concierges.length} compte(s) enregistré(s)</p>
          </div>
          <button
            onClick={openCreateConcierge}
            className="text-white px-5 py-2.5 rounded-lg font-semibold text-sm flex items-center gap-2"
            style={{ background: GRADIENT }}
          >
            <Plus size={18} /> Nouveau compte
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <div className="col-span-3">Nom</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Téléphone</div>
            <div className="col-span-2">Accès</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {concierges.length > 0 ? (
            concierges.map((c) => (
              <div
                key={c.id}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 border-b border-gray-100 hover:bg-gray-50 transition items-center"
              >
                <div className="col-span-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-sm shrink-0">
                    {(c.name || c.username || "?")[0].toUpperCase()}
                  </div>
                  <span className="font-semibold text-gray-800 text-sm">{c.name || c.username || "—"}</span>
                </div>
                <div className="col-span-3 text-sm text-gray-600">{c.email || "—"}</div>
                <div className="col-span-2 text-sm text-gray-600">{c.phone || c.telephone || "—"}</div>
                <div className="col-span-2">
                  {c.authUid ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                      <CheckCircle size={12} /> Actif
                    </span>
                  ) : (
                    <button
                      onClick={() => openCreateLogin(c)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition"
                    >
                      <KeyRound size={12} /> Créer un accès
                    </button>
                  )}
                </div>
                <div className="col-span-2 flex items-center justify-end gap-2">
                  <button
                    onClick={() => openEditConcierge(c)}
                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                    title="Modifier"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteConcierge(c.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <Building2 size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-400">Aucune conciergerie enregistrée</p>
            </div>
          )}
        </div>
      </>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // SITES
  // ══════════════════════════════════════════════════════════════════════

  function renderSites() {
    const toggleSite = (id) =>
      setExpandedSites((prev) => ({ ...prev, [id]: !prev[id] }));

    return (
      <>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Sites</h2>
            <p className="text-sm text-gray-500">{sites.length} site(s) configuré(s)</p>
          </div>
          <button
            onClick={openCreateSite}
            className="text-white px-5 py-2.5 rounded-lg font-semibold text-sm flex items-center gap-2"
            style={{ background: GRADIENT }}
          >
            <Plus size={18} /> Nouveau site
          </button>
        </div>

        {/* Date filter */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold text-gray-500 uppercase">Période :</span>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <Calendar size={14} className="text-gray-400" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="text-sm border-none outline-none bg-transparent text-gray-700 w-[130px]"
              />
            </div>
            <span className="text-gray-400 text-sm">→</span>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <Calendar size={14} className="text-gray-400" />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="text-sm border-none outline-none bg-transparent text-gray-700 w-[130px]"
              />
            </div>
            {hasDateFilter && (
              <button
                onClick={clearDateFilters}
                className="text-xs text-gray-500 hover:text-red-500 flex items-center gap-1 transition"
              >
                <X size={14} /> Effacer
              </button>
            )}
          </div>
        </div>

        <div className="space-y-3">
          {sites.map((s) => {
            const sitePrestations = filterByDate(allPrestations.filter((t) => t.site === s.name))
              .slice()
              .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
            const pending = sitePrestations.filter((t) => t.state === "En attente" || t._source === "tasksPending").length;
            const inProgress = sitePrestations.filter((t) => t.state === "En cours").length;
            const done = sitePrestations.filter((t) => t.state === "Terminé").length;
            const isOpen = !!expandedSites[s.id];

            return (
              <div key={s.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="flex items-center">
                  <button
                    onClick={() => toggleSite(s.id)}
                    className="flex-1 p-5 flex items-center justify-between hover:bg-gray-50 transition cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                        <MapPin size={20} />
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-gray-800">{s.name}</p>
                        <div className="flex items-center gap-2">
                          {s.conciergerie && (
                            <span className="text-xs text-indigo-500 font-medium">{s.conciergerie}</span>
                          )}
                          <span className="text-xs text-gray-400">{sitePrestations.length} prestation(s)</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 text-xs font-semibold">
                        {pending > 0 && (
                          <span className="bg-yellow-100 text-yellow-700 px-2.5 py-1 rounded-full flex items-center gap-1">
                            <Clock size={12} /> {pending}
                          </span>
                        )}
                        {inProgress > 0 && (
                          <span className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full flex items-center gap-1">
                            <AlertCircle size={12} /> {inProgress}
                          </span>
                        )}
                        {done > 0 && (
                          <span className="bg-green-100 text-green-700 px-2.5 py-1 rounded-full flex items-center gap-1">
                            <CheckCircle size={12} /> {done}
                          </span>
                        )}
                      </div>
                      {isOpen ? (
                        <ChevronDown size={20} className="text-gray-400" />
                      ) : (
                        <ChevronRight size={20} className="text-gray-400" />
                      )}
                    </div>
                  </button>
                  <div className="flex gap-1 pr-4">
                    <button
                      onClick={() => openEditSite(s)}
                      className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                      title="Modifier"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteSite(s.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Supprimer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-gray-100">
                    {sitePrestations.length > 0 ? (
                      <div className="divide-y divide-gray-50">
                        {sitePrestations.map((t) => {
                          const style = getStatusStyle(t.state || (t._source === "tasksPending" ? "En attente" : "Validée"));
                          const isPending = t._source === "tasksPending";
                          return (
                            <div key={`${t._source}-${t.id}`} className="px-5 py-4 flex items-center gap-4 hover:bg-gray-50 transition">
                              <div className={`w-2 h-2 rounded-full shrink-0 ${style.badge.split(" ")[0]}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-semibold text-gray-800 truncate">
                                    {t.prestationType || "Prestation"}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${style.badge} shrink-0`}>
                                    {t.state || (isPending ? "En attente" : "—")}
                                  </span>
                                </div>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <Car size={12} className="text-gray-400" />
                                    {t.carName || "—"} · {t.carImmatriculation || "—"}
                                  </span>
                                  {getConciergieName(t) && (
                                    <span className="flex items-center gap-1">
                                      <Building2 size={12} className="text-indigo-400" />
                                      {getConciergieName(t)}
                                    </span>
                                  )}
                                  {t.nameClient && (
                                    <span className="flex items-center gap-1">
                                      <User size={12} className="text-gray-400" />
                                      {t.nameClient}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right shrink-0 mr-2">
                                <p className="text-sm font-medium text-gray-700">{t.date || "—"}</p>
                                {t.heure && <p className="text-xs text-gray-400">{t.heure}</p>}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  onClick={() => { setFormData(t); setModal("viewPrestation"); }}
                                  className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                  title="Voir"
                                >
                                  <Eye size={14} />
                                </button>
                                <button
                                  onClick={() => openEditPrestation(t)}
                                  className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                  title="Modifier"
                                >
                                  <Edit2 size={14} />
                                </button>
                                {isPending && (
                                  <button
                                    onClick={() => handleValidateTask(t)}
                                    className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                                    title="Valider"
                                  >
                                    <Check size={14} />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteTask(t._source, t.id)}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                  title="Supprimer"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-6 text-center">
                        <ClipboardList size={28} className="mx-auto text-gray-200 mb-2" />
                        <p className="text-gray-400 text-sm">Aucune prestation sur ce site</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {sites.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm">
              <MapPin size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-400">Aucun site configuré</p>
            </div>
          )}
        </div>
      </>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // MODALS
  // ══════════════════════════════════════════════════════════════════════

  function renderPrestationModal() {
    const isEdit = !!formData._editId;
    return (
      <Modal title={isEdit ? "Modifier la prestation" : "Nouvelle prestation"} onClose={() => setModal(null)}>
        <div className="space-y-4">
          <FormSelect
            label="Type de prestation"
            value={formData.prestationType || ""}
            onChange={(v) => setFormData({ ...formData, prestationType: v })}
            options={PRESTATIONS}
            placeholder="Sélectionnez une prestation"
          />
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Véhicule *" value={formData.carName || ""} onChange={(v) => setFormData({ ...formData, carName: v })} placeholder="Ex : Mercedes Classe E" />
            <FormInput label="Immatriculation *" value={formData.carImmatriculation || ""} onChange={(v) => setFormData({ ...formData, carImmatriculation: v.toUpperCase() })} placeholder="Ex : AB-123-CD" />
          </div>
          <FormInput label="Client" value={formData.nameClient || ""} onChange={(v) => setFormData({ ...formData, nameClient: v })} placeholder="Nom du client" />
          <FormSelect
            label="Site"
            value={formData.site || ""}
            onChange={(v) => setFormData({ ...formData, site: v, conciergerie: siteToConciergerie[v] || formData.conciergerie || "" })}
            options={siteNames}
            placeholder="Sélectionnez un site"
          />
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Conciergerie</label>
            <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700">
              {formData.conciergerie || (formData.site && siteToConciergerie[formData.site]) || <span className="text-gray-400">Sélectionnez un site pour voir la conciergerie</span>}
            </div>
          </div>
          {isEdit && (
            <FormSelect
              label="Statut"
              value={formData.state || ""}
              onChange={(v) => setFormData({ ...formData, state: v })}
              options={STATES}
              placeholder="Sélectionnez un statut"
            />
          )}
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Date" type="date" value={formData.date || ""} onChange={(v) => setFormData({ ...formData, date: v })} />
            <FormInput label="Heure" type="time" value={formData.heure || ""} onChange={(v) => setFormData({ ...formData, heure: v })} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Notes</label>
            <textarea
              value={formData.note || ""}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              rows={3}
              placeholder="Précisions..."
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:outline-none resize-none"
            />
          </div>
          <ModalActions onCancel={() => setModal(null)} onSubmit={handleSavePrestation} submitting={submitting} label={isEdit ? "Enregistrer" : "Créer"} />
        </div>
      </Modal>
    );
  }

  function renderConciergeModal() {
    const isEdit = !!formData._editId;
    return (
      <Modal title={isEdit ? "Modifier la conciergerie" : "Nouvelle conciergerie"} onClose={() => setModal(null)}>
        <div className="space-y-4">
          <FormInput label="Nom *" value={formData.name || ""} onChange={(v) => setFormData({ ...formData, name: v })} placeholder="Ex : Conciergerie Prestige" />
          <FormInput label="Email" type="email" value={formData.email || ""} onChange={(v) => setFormData({ ...formData, email: v })} placeholder="email@conciergerie.com" />
          <FormInput label="Téléphone" value={formData.phone || ""} onChange={(v) => setFormData({ ...formData, phone: v })} placeholder="Ex : 01 23 45 67 89" />
          <ModalActions onCancel={() => setModal(null)} onSubmit={handleSaveConcierge} submitting={submitting} label={isEdit ? "Enregistrer" : "Créer"} />
        </div>
      </Modal>
    );
  }

  function renderSiteModal() {
    const isEdit = !!formData._editId;
    const conciergeOptions = concierges.map((c) => c.name || c.username || c.id);
    return (
      <Modal title={isEdit ? "Modifier le site" : "Nouveau site"} onClose={() => setModal(null)}>
        <div className="space-y-4">
          <FormInput label="Nom du site *" value={formData.name || ""} onChange={(v) => setFormData({ ...formData, name: v })} placeholder="Ex : TOUR EIFFEL BUSINESS CENTER" />
          <FormSelect
            label="Conciergerie associée"
            value={formData.conciergerie || ""}
            onChange={(v) => setFormData({ ...formData, conciergerie: v })}
            options={conciergeOptions}
            placeholder="Sélectionnez une conciergerie"
          />
          <ModalActions onCancel={() => setModal(null)} onSubmit={handleSaveSite} submitting={submitting} label={isEdit ? "Enregistrer" : "Créer"} />
        </div>
      </Modal>
    );
  }

  function renderLoginModal() {
    const conciergerie = concierges.find((c) => c.id === formData._conciergeId);
    const displayName = conciergerie?.name || conciergerie?.username || "Conciergerie";
    return (
      <Modal title={`Créer un accès — ${displayName}`} onClose={() => setModal(null)}>
        <div className="space-y-4">
          <div className="bg-indigo-50 p-3 rounded-lg flex items-center gap-2">
            <Building2 size={16} className="text-indigo-500" />
            <span className="text-sm font-medium text-indigo-700">{displayName}</span>
          </div>
          <FormInput
            label="Email de connexion *"
            type="email"
            value={formData.email || ""}
            onChange={(v) => setFormData({ ...formData, email: v })}
            placeholder="email@conciergerie.com"
          />
          <FormInput
            label="Mot de passe *"
            type="password"
            value={formData.password || ""}
            onChange={(v) => setFormData({ ...formData, password: v })}
            placeholder="Minimum 6 caractères"
          />
          <ModalActions onCancel={() => setModal(null)} onSubmit={handleCreateLogin} submitting={submitting} label="Créer l'accès" />
        </div>
      </Modal>
    );
  }

  function renderViewPrestationModal() {
    const t = formData;
    const style = getStatusStyle(t.state);
    return (
      <Modal title="Détails de la prestation" onClose={() => setModal(null)}>
        <div className="space-y-4">
          {t.prestationType && (
            <div className="flex items-center gap-2 text-indigo-600 font-semibold">
              <Sparkles size={16} />
              {t.prestationType}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <InfoBox label="VÉHICULE" value={t.carName} />
            <InfoBox label="IMMATRICULATION" value={t.carImmatriculation} />
            <InfoBox label="CLIENT" value={t.nameClient} />
            <InfoBox label="SITE" value={t.site} />
            <InfoBox label="DATE" value={`${t.date || "—"}${t.heure ? ` à ${t.heure}` : ""}`} />
            <InfoBox label="STATUT" value={
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${style.badge}`}>
                {t.state || "—"}
              </span>
            } />
          </div>
          {getConciergieName(t) && (
            <div className="bg-indigo-50 p-3 rounded-lg flex items-center gap-2">
              <Building2 size={16} className="text-indigo-500" />
              <span className="text-sm font-medium text-indigo-700">Conciergerie : {getConciergieName(t)}</span>
            </div>
          )}
          {t.note && (
            <div className="bg-gray-50 p-3 rounded-lg flex items-start gap-2">
              <FileText size={16} className="text-gray-400 mt-0.5 shrink-0" />
              <span className="text-sm text-gray-600">{t.note}</span>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setModal(null)}
              className="flex-1 px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold text-sm hover:bg-gray-50 transition"
            >
              Fermer
            </button>
            <button
              onClick={() => { openEditPrestation(t); }}
              className="flex-1 text-white px-4 py-2.5 rounded-lg font-semibold text-sm transition"
              style={{ background: GRADIENT }}
            >
              Modifier
            </button>
          </div>
        </div>
      </Modal>
    );
  }
}

// ══════════════════════════════════════════════════════════════════════════
// Shared UI components
// ══════════════════════════════════════════════════════════════════════════

function StatCard({ label, value, from, to, sub }) {
  return (
    <div className={`bg-gradient-to-br ${from} ${to} rounded-xl shadow-lg p-6 text-white`}>
      <p className={`${sub} text-sm font-medium mb-1`}>{label}</p>
      <p className="text-4xl font-bold">{value}</p>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="text-white p-5 rounded-t-2xl" style={{ background: "linear-gradient(135deg, #667eea 0%, #142C93 100%)" }}>
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold">{title}</h2>
            <button onClick={onClose} className="hover:bg-white hover:bg-opacity-20 rounded-lg p-1.5 transition">
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function FormInput({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:outline-none transition"
      />
    </div>
  );
}

function FormSelect({ label, value, onChange, options, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:outline-none transition"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}

function ModalActions({ onCancel, onSubmit, submitting, label }) {
  return (
    <div className="flex gap-3 pt-2">
      <button
        type="button"
        onClick={onCancel}
        className="flex-1 px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold text-sm hover:bg-gray-50 transition"
      >
        Annuler
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={submitting}
        className="flex-1 text-white px-4 py-2.5 rounded-lg font-semibold text-sm transition disabled:opacity-50"
        style={{ background: "linear-gradient(135deg, #667eea 0%, #142C93 100%)" }}
      >
        {submitting ? "Envoi..." : label}
      </button>
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="bg-gray-50 p-3 rounded-lg">
      <p className="text-xs text-gray-500 font-semibold mb-1">{label}</p>
      <div className="text-sm font-bold text-gray-800">{value || "—"}</div>
    </div>
  );
}
